import type { Route } from "../../store/admin/routesStore";
import type { Trip } from "../../store/admin/tripsStore";
import type { GeoPointInput, QuoteCalcInput, QuoteSchedule } from "../../features/quotes/types";

/** De dónde nace una cotización: una ruta, un viaje o desde cero. */
export type QuoteSource =
  | { kind: "route"; route: Route }
  | { kind: "trip"; trip: Trip; route?: Route | null }
  | { kind: "blank"; crm_company_id?: string | null; crm_contact_id?: string | null };

function toPoint(p: { latitud?: number; longitud?: number; latitude?: number; longitude?: number } | undefined): GeoPointInput | null {
  if (!p) return null;
  const lat = p.latitud ?? p.latitude;
  const lng = p.longitud ?? p.longitude;
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  return { lat, lng };
}

/** Extremos de una ruta guardada (sin dirección: el backend acepta sólo coordenadas). */
export function routeEndpoints(route: Route | null | undefined): { origin: GeoPointInput | null; destination: GeoPointInput | null } {
  const pts = route?.principal_route ?? [];
  if (pts.length === 0) return { origin: null, destination: null };
  return {
    origin: toPoint(pts[0] as never),
    destination: pts.length > 1 ? toPoint(pts[pts.length - 1] as never) : null,
  };
}

/** Convierte `YYYY-MM-DD` + `HH:mm` de una asignación en ISO local. */
function assignmentIso(date?: string, time?: string): string | null {
  if (!date) return null;
  const t = time && /^\d{2}:\d{2}/.test(time) ? time.slice(0, 5) : "08:00";
  const d = new Date(`${date}T${t}:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Horario prellenado desde la primera asignación del viaje. */
export function tripSchedule(trip: Trip | null | undefined): QuoteSchedule | null {
  const a = trip?.assignments?.[0];
  if (!a) return null;
  return {
    pickup_at: assignmentIso(a.date, a.start_time),
    delivery_at: assignmentIso(a.date, a.end_time),
    timezone: (a as { timezone?: string }).timezone ?? null,
  };
}

/** Insumos iniciales del cálculo según el origen de la cotización. */
export function initialCalcInput(source: QuoteSource): QuoteCalcInput {
  if (source.kind === "route") {
    return { route_id: source.route.id };
  }
  if (source.kind === "trip") {
    return {
      trip_id: source.trip.id,
      route_id: source.trip.route_id ?? source.route?.id ?? null,
      schedule: tripSchedule(source.trip),
    };
  }
  return {};
}

export function sourceLabel(source: QuoteSource): string | null {
  if (source.kind === "route") return source.route.name;
  if (source.kind === "trip") return source.trip.title ?? null;
  return null;
}

/** `datetime-local` ⇄ ISO. */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
